/**
 * Effy Order subscription plans — the single source of plan data for the UI.
 *
 * Used by:
 *  - the public pricing page (EffyOrderPricing.js)
 *  - the "Choose a plan" picker on Edit Profile (SubscriptionPlanPicker.js), which charges `amount` via Razorpay
 *
 * `planCode` must match the backend SubscriptionPlan enum, and `amount` must match
 * subscription.plan.price.* in the backend application.properties. If the amounts ever differ,
 * the backend refuses to start the payment rather than charging the wrong amount.
 *
 * Plans are listed from the cheapest to the most expensive.
 */
export const EFFY_ORDER_PLANS = [
  {
    id: 'lite',
    planCode: 'LITE',
    name: 'Lite',
    amount: 199,
    period: 'per month',
    trialText: 'Start with 30-day free trial',
    description: 'Perfect for getting a feel for the system and starting small.',
    features: [
      'Basic Dashboard analytics',
      'Up to 500 orders per month',
      'Single owner login',
      'Invoice generation',
      'Basic Inventory tracking',
      'Email support'
    ],
    cta: 'Start Free Trial',
    highlight: false,
    gstNote: null,
  },
  {
    id: 'pro',
    planCode: 'PRO',
    name: 'Pro',
    amount: 249,
    period: 'per month',
    trialText: 'Start with 30-day free trial',
    description: 'Ideal for growing businesses needing automated communication.',
    features: [
      'Unlimited orders',
      'Full order & inventory management',
      'Excel bulk upload',
      'WhatsApp & email notifications',
      'Low stock alerts',
      'GST & sales reports',
      'Employee tracking & sales per employee',
      'Priority email support'
    ],
    cta: 'Start Free Trial',
    highlight: true,
    gstNote: 'GST included',
  },
  {
    id: 'growth',
    planCode: 'GROWTH',
    name: 'Growth',
    amount: 349,
    period: 'per month',
    trialText: null,
    description: 'For shops that sell at the counter and want a teammate on the system.',
    features: [
      'Everything in Pro',
      'POS barcode scan on sale orders',
      'Product photos — up to 3 per item',
      'GST number validation with customer auto-fill',
      '1 additional user account'
    ],
    cta: 'Get Growth',
    highlight: false,
    gstNote: 'GST included',
  },
  {
    id: 'premium',
    planCode: 'PREMIUM',
    name: 'Premium',
    amount: 699,
    period: 'per month',
    trialText: null,
    description: 'The complete suite for high-volume operations and deep reporting.',
    features: [
      'Everything in Growth',
      'Unlimited WhatsApp notifications',
      '2 additional user accounts',
      'Full GST & sales reports',
      'Barcode printing & delivery challans',
      'Advanced analytics dashboard',
      'Customer-wise sales reports',
      'Dedicated support team',
      'Custom feature development in existing product'
    ],
    cta: 'Talk to Sales',
    ctaRoute: '/contact',
    highlight: false,
    gstNote: 'GST included',
  }
];

// ₹199 → "₹199", ₹1499 → "₹1,499"
export function formatPlanPrice(amount) {
  return '₹' + Number(amount).toLocaleString('en-IN');
}

// Yearly payment: the user pays for 11 months and gets 12 (1 month free).
// Must match subscription.annual.paid.months in the backend application.properties.
export const ANNUAL_PAID_MONTHS = 11;
const MONTHS_IN_YEAR = 12;

export const BILLING_MONTHLY = 'MONTHLY';
export const BILLING_ANNUAL = 'ANNUAL';

/**
 * Price to show and charge for a plan and billing cycle.
 *  monthly: original = discounted = monthly amount, saved = 0
 *  yearly : original = monthly x 12 (struck out), discounted = monthly x 11 (what is charged), saved = one month
 */
export function getPlanPriceForCycle(plan, cycle) {
  if (cycle === BILLING_ANNUAL) {
    return {
      original: plan.amount * MONTHS_IN_YEAR,
      discounted: plan.amount * ANNUAL_PAID_MONTHS,
      saved: plan.amount * (MONTHS_IN_YEAR - ANNUAL_PAID_MONTHS),
    };
  }
  return { original: plan.amount, discounted: plan.amount, saved: 0 };
}
